/**
 * useBooking Hook
 * Manages booking functionality with Jitsi integration
 */

import { useState, useCallback, useEffect } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  setDoc,
  Timestamp,
  getDoc,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { generateJitsiMeetingUrl, generateJitsiRoomName } from '@/utils/jitsi';
import { invokeFunctionSilent } from '@/lib/cloudrun';
import {
  BookingRequest,
  BookingConfirmation,
  UserAvailability,
  TimeSlot,
  DEFAULT_AVAILABILITY,
} from '@/types/booking.types';
import { MeetingStatus } from '@/types/calendar.types';

export function useBooking(hostId?: string) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [availability, setAvailability] = useState<UserAvailability | null>(null);
  const [existingMeetings, setExistingMeetings] = useState<Array<{ date: string; time: string; duration: number }>>([]);

  // Fetch host availability
  useEffect(() => {
    if (!hostId) return;

    const fetchAvailability = async () => {
      try {
        const docRef = doc(db, 'userAvailability', hostId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setAvailability(docSnap.data() as UserAvailability);
        } else {
          setAvailability({
            userId: hostId,
            slots: DEFAULT_AVAILABILITY,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            slotDuration: 30,
            bufferBetweenMeetings: 15,
          });
        }
      } catch (error) {
        console.error('Error fetching availability:', error);
      }
    };

    fetchAvailability();
  }, [hostId]);

  // Fetch existing meetings for a specific date
  const fetchMeetingsForDate = useCallback(async (date: string): Promise<void> => {
    if (!hostId) return;

    try {
      const q = query(
        collection(db, 'meetings'),
        where('participants', 'array-contains', hostId),
        where('date', '==', date),
        where('status', 'in', ['pending', 'confirmed'])
      );

      const snapshot = await getDocs(q);
      const meetings = snapshot.docs.map((doc) => ({
        date: doc.data().date,
        time: doc.data().time,
        duration: doc.data().duration || 30,
      }));

      setExistingMeetings(meetings);
    } catch (error) {
      console.error('Error fetching meetings:', error);
    }
  }, [hostId]);

  // Get available time slots for a date
  const getAvailableSlots = useCallback((date: Date): TimeSlot[] => {
    if (!availability) return [];

    const dayOfWeek = date.getDay();
    const dayAvailability = availability.slots.filter((s) => s.dayOfWeek === dayOfWeek);

    if (dayAvailability.length === 0) return [];

    const slots: TimeSlot[] = [];
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    for (const slot of dayAvailability) {
      const [startHour, startMin] = slot.startTime.split(':').map(Number);
      const [endHour, endMin] = slot.endTime.split(':').map(Number);

      let currentHour = startHour;
      let currentMin = startMin;

      while (currentHour < endHour || (currentHour === endHour && currentMin < endMin)) {
        const timeStr = `${currentHour.toString().padStart(2, '0')}:${currentMin.toString().padStart(2, '0')}`;

        if (isToday) {
          const slotTime = new Date(date);
          slotTime.setHours(currentHour, currentMin, 0, 0);
          if (slotTime <= now) {
            currentMin += availability.slotDuration + availability.bufferBetweenMeetings;
            if (currentMin >= 60) {
              currentHour += Math.floor(currentMin / 60);
              currentMin = currentMin % 60;
            }
            continue;
          }
        }

        const isConflicting = existingMeetings.some((meeting) => {
          const [meetingHour, meetingMin] = meeting.time.split(':').map(Number);
          const meetingStart = meetingHour * 60 + meetingMin;
          const meetingEnd = meetingStart + meeting.duration;
          const slotStart = currentHour * 60 + currentMin;
          const slotEnd = slotStart + availability.slotDuration;

          return (
            (slotStart >= meetingStart && slotStart < meetingEnd) ||
            (slotEnd > meetingStart && slotEnd <= meetingEnd) ||
            (slotStart <= meetingStart && slotEnd >= meetingEnd)
          );
        });

        slots.push({
          time: timeStr,
          available: !isConflicting,
        });

        currentMin += availability.slotDuration + availability.bufferBetweenMeetings;
        if (currentMin >= 60) {
          currentHour += Math.floor(currentMin / 60);
          currentMin = currentMin % 60;
        }
      }
    }

    return slots;
  }, [availability, existingMeetings]);

  // Book a meeting
  const bookMeeting = useCallback(async (request: BookingRequest): Promise<BookingConfirmation | null> => {
    setLoading(true);

    try {
      const roomName = generateJitsiRoomName(request.title);
      const meetingUrl = generateJitsiMeetingUrl(roomName);

      const meetingData = {
        title: request.title,
        description: request.description || '',
        dealId: request.dealId || null,
        date: request.date,
        time: request.time,
        duration: request.duration,
        type: 'buyer_seller',
        organizerId: request.hostId,
        participants: [request.hostId],
        status: 'confirmed' as MeetingStatus,
        meetingUrl,
        calendarProvider: 'internal',
        guestName: request.guestName,
        guestEmail: request.guestEmail,
        guestPhone: request.guestPhone || null,
        createdAt: Timestamp.now(),
        confirmedAt: Timestamp.now(),
      };

      const docRef = await addDoc(collection(db, 'meetings'), meetingData);

      // Fetch host name and email
      let hostName = 'Host';
      let hostEmail: string | undefined;
      try {
        const usersQuery = query(collection(db, 'users'), where('id', '==', request.hostId));
        const usersSnapshot = await getDocs(usersQuery);
        if (!usersSnapshot.empty) {
          const hostData = usersSnapshot.docs[0].data();
          hostName = hostData.name || hostData.email || 'Host';
          hostEmail = hostData.email;
        }
      } catch {
        // Use default
      }

      // Send confirmation email
      try {
        await invokeFunctionSilent('send-booking-confirmation', {
          guestEmail: request.guestEmail,
          guestName: request.guestName,
          hostName,
          hostEmail,
          meetingTitle: request.title,
          meetingDate: request.date,
          meetingTime: request.time,
          duration: request.duration,
          meetingUrl,
        });
      } catch (emailErr) {
        console.error('Failed to send confirmation email:', emailErr);
      }

      // Create notification for host
      await addDoc(collection(db, 'userNotifications'), {
        userId: request.hostId,
        type: 'meeting_booked',
        title: 'New Meeting Booked',
        message: `${request.guestName} has booked a meeting: ${request.title}`,
        relatedMeetingId: docRef.id,
        createdAt: Timestamp.now(),
        seen: false,
      });

      toast({
        title: 'Meeting Booked!',
        description: 'Your meeting has been confirmed. A confirmation email has been sent.',
      });

      return {
        id: docRef.id,
        meetingId: docRef.id,
        meetingUrl,
        date: request.date,
        time: request.time,
        duration: request.duration,
        title: request.title,
        hostName,
        guestName: request.guestName,
        guestEmail: request.guestEmail,
      };
    } catch (error) {
      console.error('Error booking meeting:', error);
      toast({
        title: 'Error',
        description: 'Failed to book meeting. Please try again.',
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Save user availability
  const saveAvailability = useCallback(async (data: Omit<UserAvailability, 'userId'>): Promise<boolean> => {
    if (!user?.id) return false;

    try {
      const docRef = doc(db, 'userAvailability', user.id);
      // setDoc + merge garantit que le document est TOUJOURS écrit à
      // userAvailability/{uid} — jamais un ID aléatoire — que le document
      // existe déjà ou non. Ajoute aussi role/displayName, requis pour que
      // getPublicStaffAvailability (page /contact) puisse retrouver ce
      // membre du staff.
      await setDoc(docRef, {
        ...data,
        userId: user.id,
        role: (user as any)?.roles?.[0] || '',
        displayName: user.displayName || (user as any)?.email || '',
      }, { merge: true });

      toast({
        title: 'Availability Saved',
        description: 'Your availability has been updated.',
      });

      return true;
    } catch (error) {
      console.error('Error saving availability:', error);
      toast({
        title: 'Error',
        description: 'Failed to save availability.',
        variant: 'destructive',
      });
      return false;
    }
  }, [user?.id, toast]);

  return {
    loading,
    availability,
    getAvailableSlots,
    fetchMeetingsForDate,
    bookMeeting,
    saveAvailability,
  };
}